// src/features/authn/logic.ts
// Validation, formatters, status maps, auth flow helpers.
// Framework-agnostic — NO React imports.

import type { SignInFormState, SignUpFormState, ResetPasswordFormState, NewPasswordFormState, UpdatePasswordFormState } from './types';

// ============================================
// Validation
// ============================================

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateSignIn(data: SignInFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.email.trim()) errors.email = 'auth.emailRequired';
  else if (!EMAIL_RE.test(data.email)) errors.email = 'auth.emailInvalid';
  if (!data.password) errors.password = 'auth.passwordRequired';
  return errors;
}

export function validateSignUp(data: SignUpFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.email.trim()) errors.email = 'auth.emailRequired';
  else if (!EMAIL_RE.test(data.email)) errors.email = 'auth.emailInvalid';
  if (!data.username.trim()) errors.username = 'auth.usernameRequired';
  else if (data.username.length < 3) errors.username = 'auth.usernameTooShort';
  if (!data.password) errors.password = 'auth.passwordRequired';
  else if (data.password.length < 8) errors.password = 'auth.passwordTooShort';
  if (data.password !== data.passwordConfirmation) errors.passwordConfirmation = 'auth.passwordsMismatch';
  if (!data.acceptTerms) errors.acceptTerms = 'auth.acceptTermsRequired';
  return errors;
}

export function validatePasswordReset(data: ResetPasswordFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.email.trim()) errors.email = 'auth.emailRequired';
  else if (!EMAIL_RE.test(data.email)) errors.email = 'auth.emailInvalid';
  return errors;
}

export function validateNewPassword(data: NewPasswordFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.newPassword) errors.newPassword = 'auth.passwordRequired';
  else if (data.newPassword.length < 8) errors.newPassword = 'auth.passwordTooShort';
  if (data.newPassword !== data.passwordConfirmation) errors.passwordConfirmation = 'auth.passwordsMismatch';
  return errors;
}

export function validateUpdatePassword(data: UpdatePasswordFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.oldPassword) errors.oldPassword = 'auth.currentPasswordRequired';
  if (!data.newPassword) errors.newPassword = 'auth.passwordRequired';
  else if (data.newPassword.length < 8) errors.newPassword = 'auth.passwordTooShort';
  if (data.newPassword !== data.passwordConfirmation) errors.passwordConfirmation = 'auth.passwordsMismatch';
  return errors;
}

// ============================================
// Login result status map
// ============================================

export const loginResultStatusMap: Record<string, { labelKey: string; variant: string }> = {
  LOGIN_RESULT_UNSPECIFIED: { labelKey: 'auth.loginResultUnknown', variant: 'secondary' },
  LOGIN_RESULT_SUCCESS: { labelKey: 'auth.loginResultSuccess', variant: 'success' },
  LOGIN_RESULT_FAILED: { labelKey: 'auth.loginResultFailed', variant: 'error' },
  LOGIN_RESULT_BLOCKED: { labelKey: 'auth.loginResultBlocked', variant: 'warning' },
  LOGIN_RESULT_2FA_REQUIRED: { labelKey: 'auth.loginResult2FA', variant: 'info' },
};

export function formatLoginResult(result: string): { labelKey: string; variant: string } {
  return loginResultStatusMap[result] ?? loginResultStatusMap.LOGIN_RESULT_UNSPECIFIED;
}

// ============================================
// Two-factor method map
// ============================================

export const twoFactorMethodMap: Record<string, { labelKey: string; icon: string }> = {
  TWO_FACTOR_METHOD_UNSPECIFIED: { labelKey: 'auth.2faMethodUnknown', icon: 'help' },
  TWO_FACTOR_METHOD_TOTP: { labelKey: 'auth.2faMethodTotp', icon: 'smartphone' },
  TWO_FACTOR_METHOD_SMS: { labelKey: 'auth.2faMethodSms', icon: 'message' },
  TWO_FACTOR_METHOD_EMAIL: { labelKey: 'auth.2faMethodEmail', icon: 'mail' },
};

// ============================================
// Session info formatter
// ============================================

export function formatSessionInfo(userAgent: string): { device: string; browser: string; os: string } {
  let browser = 'Unknown';
  let os = 'Unknown';
  let device = 'Desktop';

  if (/Mobile|Android|iPhone|iPad/i.test(userAgent)) device = 'Mobile';
  else if (/Tablet|iPad/i.test(userAgent)) device = 'Tablet';

  if (/Chrome/i.test(userAgent) && !/Edg/i.test(userAgent)) browser = 'Chrome';
  else if (/Firefox/i.test(userAgent)) browser = 'Firefox';
  else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) browser = 'Safari';
  else if (/Edg/i.test(userAgent)) browser = 'Edge';

  if (/Windows/i.test(userAgent)) os = 'Windows';
  else if (/Mac OS/i.test(userAgent)) os = 'macOS';
  else if (/Linux/i.test(userAgent)) os = 'Linux';
  else if (/Android/i.test(userAgent)) os = 'Android';
  else if (/iPhone|iPad/i.test(userAgent)) os = 'iOS';

  return { device, browser, os };
}

// ============================================
// Login record formatter
// ============================================

export function formatLoginRecord(record: {
  result?: string;
  ip_address?: string;
  user_agent?: string;
  timestamp?: string;
}): {
  resultInfo: { labelKey: string; variant: string };
  sessionInfo: { device: string; browser: string; os: string };
  ip: string;
  time: string;
} {
  return {
    resultInfo: formatLoginResult(record.result ?? ''),
    sessionInfo: formatSessionInfo(record.user_agent ?? ''),
    ip: record.ip_address ?? '',
    time: record.timestamp ?? '',
  };
}

// ============================================
// Password strength calculator
// ============================================

export function calculatePasswordStrength(password: string): number {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  return Math.min(score, 4);
}

export const passwordStrengthLabels: Record<number, string> = {
  0: 'auth.passwordStrengthNone',
  1: 'auth.passwordStrengthWeak',
  2: 'auth.passwordStrengthFair',
  3: 'auth.passwordStrengthGood',
  4: 'auth.passwordStrengthStrong',
};
